import React, { useState, useMemo } from 'react';
import { useSoloTemplates } from '@hooks/useSoloTemplates';
import { SoloLength, SoloInstrument, SOLO_INSTRUMENT_LABELS } from '@/types/soloChoreographer';
import SoloTemplateCard from './SoloTemplateCard';
import { Modal, Input, Button } from '../../../components/ui';
import { Search, Guitar, Filter } from 'lucide-react';

interface SoloTemplateBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (templateId: string) => void;
  selectedId?: string | null;
}

const LENGTH_FILTERS: { label: string; value: SoloLength | null }[] = [
  { label: 'All', value: null },
  { label: '4-Bar', value: 4 },
  { label: '8-Bar', value: 8 },
  { label: '12-Bar', value: 12 },
  { label: '16-Bar', value: 16 },
];

const SoloTemplateBrowser: React.FC<SoloTemplateBrowserProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedId,
}) => {
  const { data: soloTemplates } = useSoloTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [lengthFilter, setLengthFilter] = useState<SoloLength | null>(null);
  const [instrumentFilter, setInstrumentFilter] = useState<SoloInstrument | null>(null);

  const allInstruments = useMemo(() => {
    const set = new Set<SoloInstrument>();
    soloTemplates.forEach(t => set.add(t.defaultInstrument));
    return Array.from(set).sort((a, b) =>
      SOLO_INSTRUMENT_LABELS[a].localeCompare(SOLO_INSTRUMENT_LABELS[b])
    );
  }, [soloTemplates]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return soloTemplates.filter(template => {
      const matchesSearch = !term || [
        template.name,
        template.description,
        SOLO_INSTRUMENT_LABELS[template.defaultInstrument],
        ...template.phrases.map(p => p.role),
        ...template.phrases.map(p => p.name),
      ].some(field => field.toLowerCase().includes(term));

      const matchesLength = !lengthFilter || template.length === lengthFilter;
      const matchesInstrument = !instrumentFilter || template.defaultInstrument === instrumentFilter;

      return matchesSearch && matchesLength && matchesInstrument;
    });
  }, [soloTemplates, searchTerm, lengthFilter, instrumentFilter]);

  const totalCount = soloTemplates.length;

  const handleSelect = (id: string) => {
    onSelect(id);
    onClose();
    setSearchTerm('');
    setLengthFilter(null);
    setInstrumentFilter(null);
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setLengthFilter(null);
    setInstrumentFilter(null);
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-accent/20 rounded-xl text-accent">
        <Guitar size={20} />
      </div>
      <div>
        <span className="text-xl font-black text-slate-50 uppercase tracking-tighter">
          Select Solo Template
        </span>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">
          Browse choreographed solo blueprints
        </p>
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex justify-between items-center">
      <p className="text-[11px] text-slate-400 font-bold uppercase">
        Showing {filtered.length} of {totalCount} templates
      </p>
      <Button variant="secondary" size="sm" onClick={handleClose}>
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="full"
      footer={footerContent}
      className="max-h-[85vh] flex flex-col rounded-3xl"
    >
      {/* Custom Header */}
      <div className="px-2 pb-5 border-b border-slate-800 -mt-2 mb-4">
        {headerContent}
      </div>

      {/* Search + Length Filter */}
      <div className="pb-5 border-b border-slate-800 mb-4 space-y-3">
        <Input
          placeholder="Search by name, instrument, phrase role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftElement={<Search size={18} className="text-slate-400" />}
          autoFocus
          className="rounded-2xl py-3.5"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Length</span>
          {LENGTH_FILTERS.map(f => (
            <button
              key={f.label}
              onClick={() => setLengthFilter(lengthFilter === f.value ? null : f.value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                lengthFilter === f.value
                  ? 'bg-accent/20 text-accent border border-accent/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Instrument</span>
          <button
            onClick={() => setInstrumentFilter(null)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
              !instrumentFilter
                ? 'bg-accent/20 text-accent border border-accent/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
            }`}
          >
            All
          </button>
          {allInstruments.map(inst => (
            <button
              key={inst}
              onClick={() => setInstrumentFilter(instrumentFilter === inst ? null : inst)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                instrumentFilter === inst
                  ? 'bg-accent/20 text-accent border border-accent/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
              }`}
            >
              {SOLO_INSTRUMENT_LABELS[inst]}
            </button>
          ))}
        </div>
      </div>

      {/* Card Grid */}
      <div className="flex-grow overflow-y-auto custom-scrollbar -mx-2 px-2">
        {filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-700">
            <Guitar size={48} className="opacity-20 mb-4" />
            <p className="font-black uppercase tracking-widest text-sm">No matching templates</p>
            <p className="text-[11px] text-slate-400 mt-2">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(template => (
              <SoloTemplateCard
                key={template.id}
                template={template}
                isSelected={selectedId === template.id}
                onClick={() => handleSelect(template.id)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SoloTemplateBrowser;
